/**
 * FloorLayout.vue で使用するユーティリティ関数
 * テスト可能にするため setup から切り出し
 */

const COLS = 3
// 卓同士の間隔・卓とゾーン枠の距離（上下左右の余白に効く）
const GAP = 20

/**
 * tableCount 分の卓レイアウトに不足分（1..tableCount のうち未配置のもの）を補完する。
 * 既存の table_number > tableCount の卓は維持（ユーザー設定の縮小操作を破壊しない）。
 *
 * @param {Array} layouts        既存の tableLayouts
 * @param {number} tableCount    Hall.tableCount
 * @param {Object} zone          { x, y, w, h } 配置先ゾーン
 * @param {Object} defaults      { w, h } 補完時のデフォルトサイズ
 * @returns {Array} 補完済みレイアウト配列
 */
export const fillMissingTableLayouts = (layouts, tableCount, zone, defaults) => {
  const existing = layouts || []
  const placed = new Set(existing.map(l => l.table_number))
  const result = [...existing]
  let addedIdx = 0

  for (let tn = 1; tn <= tableCount; tn += 1) {
    if (!placed.has(tn)) {
      result.push({
        table_number: tn,
        x: (zone.x || 0) + GAP + (addedIdx % COLS) * (defaults.w + GAP),
        y: (zone.y || 0) + GAP + Math.floor(addedIdx / COLS) * (defaults.h + GAP),
        w: defaults.w,
        h: defaults.h,
      })
      addedIdx += 1
    }
  }

  return result
}

/**
 * ゾーンが全レイアウトを内包するように、必要に応じて w / h を拡張した新しいゾーンを返す。
 * 既存サイズより小さくはしない（縮小はしない）。
 *
 * @param {Object} zone        { x, y, w, h }
 * @param {Array}  layouts     [{ x, y, w, h }, ...]
 * @param {number} padding     ゾーン端からの余白
 * @returns {Object} 拡張後のゾーン（新しいオブジェクト）
 */
/**
 * tableCount に対して fillMissingTableLayouts + expandZoneToFitLayouts が
 * 生成するゾーンサイズを事前に予測する。
 * 新規ゾーンを縦配置する際の高さ確保に使用。
 *
 * @param {number} tableCount
 * @param {Object} defaults  { w, h } 卓のデフォルトサイズ
 * @param {Object} opts      { cols, innerGap, padding }
 * @returns {{ w: number, h: number }}
 */
export const predictExpandedZoneSize = (tableCount, defaults, opts = {}) => {
  const cols = opts.cols || COLS
  const innerGap = opts.innerGap !== undefined ? opts.innerGap : GAP
  const padding = opts.padding !== undefined ? opts.padding : 20

  const tc = Math.max(0, tableCount || 0)
  const usedCols = tc === 0 ? 1 : Math.min(cols, tc)
  const rows = Math.max(1, Math.ceil(tc / cols))

  return {
    w: usedCols * (defaults.w + innerGap) + padding,
    h: rows * (defaults.h + innerGap) + padding,
  }
}

/**
 * 各ホールの floor_zone (primary) と floor_zone.sub_zones を一つのフラットな
 * ゾーン配列に展開する。サブゾーンはプライマリの色を継承し、ラベルは
 * 「ホール名 (N)」形式（N=2,3,...）。
 *
 * @param {Array} halls           [{ id, attributes: { name, floorZone } }, ...]
 * @param {Array<string>} defaultColors floor_zone.color が無いときのフォールバック色
 * @returns {Array} フラット化されたゾーン情報配列
 */
export const flattenHallZones = (halls, defaultColors = []) => {
  const result = []
  halls.forEach((hall, hallIdx) => {
    const fz = hall.attributes && hall.attributes.floorZone
    if (!fz || typeof fz !== 'object' || Array.isArray(fz)) return

    const fallbackColor = defaultColors[hallIdx % (defaultColors.length || 1)] || '#334155'
    const color = fz.color || fallbackColor

    result.push({
      x: fz.x || 0,
      y: fz.y || 0,
      w: fz.w || 0,
      h: fz.h || 0,
      color,
      _hallId: hall.id,
      _hallIdx: hallIdx,
      _name: hall.attributes.name,
      _zoneIdx: 0,
      _isPrimary: true,
    })

    const subs = Array.isArray(fz.sub_zones) ? fz.sub_zones : []
    subs.forEach((sub, subIdx) => {
      result.push({
        x: sub.x || 0,
        y: sub.y || 0,
        w: sub.w || 0,
        h: sub.h || 0,
        color,
        _hallId: hall.id,
        _hallIdx: hallIdx,
        _name: `${hall.attributes.name} (${subIdx + 2})`,
        _zoneIdx: subIdx + 1,
        _isPrimary: false,
        _subIdx: subIdx,
      })
    })
  })

  return result
}

/**
 * 卓レイアウトのうち、いずれかのゾーン内に中心が含まれるものを除外して返す。
 * プライマリゾーンの自動拡張時に、サブゾーンに移動された卓を除外するために使用。
 *
 * @param {Array} layouts        [{ x, y, w, h }, ...]
 * @param {Array} excludeZones   [{ x, y, w, h }, ...]
 * @returns {Array} excludeZones いずれにも含まれない layouts
 */
export const filterLayoutsOutsideZones = (layouts, excludeZones) => {
  if (!layouts || layouts.length === 0) return []
  if (!excludeZones || excludeZones.length === 0) return layouts

  return layouts.filter(l => {
    const cx = (l.x || 0) + (l.w || 0) / 2
    const cy = (l.y || 0) + (l.h || 0) / 2

    return !excludeZones.some(z => {
      const zx = z.x || 0
      const zy = z.y || 0
      const zw = z.w || 0
      const zh = z.h || 0

      return cx >= zx && cx <= zx + zw && cy >= zy && cy <= zy + zh
    })
  })
}

export const expandZoneToFitLayouts = (zone, layouts, padding = 20) => {
  const result = { ...zone }
  if (!layouts || layouts.length === 0) return result

  let maxRight = 0
  let maxBottom = 0
  layouts.forEach(l => {
    maxRight = Math.max(maxRight, (l.x || 0) + (l.w || 0))
    maxBottom = Math.max(maxBottom, (l.y || 0) + (l.h || 0))
  })

  const requiredW = maxRight + padding - (zone.x || 0)
  const requiredH = maxBottom + padding - (zone.y || 0)

  result.w = Math.max(zone.w || 0, requiredW)
  result.h = Math.max(zone.h || 0, requiredH)

  return result
}
